import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Pagination,
  MenuItem,
  Select,
  Tooltip,
  InputLabel,
  FormControl,
  Chip,
} from "@mui/material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import {
  useFetchSmsUploadDataQuery,
  useDeleteSmsUploadMutation,
} from "../store/services/smsApi";
import { cellSX, layoutCSS } from "../component/common/commonStyle";
import { useGetCustomerLogDeleteKeyQuery } from "../store/services/reportApi";
import { useGetrcsscheduleQuery } from "../store/services/rcstemplateApi";
import {
  useLazyGetwhatsappscheduledcampaignsQuery,
  useUpdateschedulecampaignMutation,
} from "../store/services/whatsappApi";
import { useLazyGetscheduleflowreportQuery } from "../store/services/flowApi";
import { jwtDecode } from "jwt-decode";

dayjs.extend(utc);

// ── Status chip colors ──────────────────────────────────────────────────────
const statusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "scheduled": return "warning";
    case "completed": return "success";
    case "failed":    return "error";
    case "pending":   return "default";
    default:          return "info";
  }
};

const WHATSAPP_STATUSES = [
  "scheduled",
  "pending",
  "processing",
  "cancelled",
  "completed",
  "failed",
];

const DEFAULT_WHATSAPP_STATUSES = ["scheduled", "pending", "processing"];

// ── Order chip colors – cycle through a small palette ───────────────────────
const ORDER_CHIP_COLORS = ["#dcfce7", "#dbeafe", "#fef9c3", "#fce7f3", "#ede9fe"];
const ORDER_TEXT_COLORS = ["#166534", "#1e40af", "#854d0e", "#9d174d", "#4c1d95"];

const OrderChip = ({ label, colorIdx = 0 }) => {
  if (!label) return <Typography variant="caption" sx={{ color: "#94a3b8" }}>—</Typography>;
  const bg   = ORDER_CHIP_COLORS[colorIdx % ORDER_CHIP_COLORS.length];
  const text = ORDER_TEXT_COLORS[colorIdx % ORDER_TEXT_COLORS.length];
  return (
    <Chip
      label={label.toUpperCase()}
      size="small"
      sx={{
        height: 20,
        fontSize: "0.65rem",
        bgcolor: bg,
        color: text,
        fontWeight: 700,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ── Helper: extract all order keys (order1, order2, …) from dataset ──────────
const getOrderKeys = (data = []) => {
  const keys = new Set();
  data.forEach((row) =>
    Object.keys(row).forEach((k) => { if (/^order\d+$/.test(k)) keys.add(k); })
  );
  return [...keys].sort((a, b) => {
    const na = parseInt(a.replace("order", ""), 10);
    const nb = parseInt(b.replace("order", ""), 10);
    return na - nb;
  });
};

// ── Table sizing (SMS / RCS / Flow) ─────────────────────────────────────────
// Every column starts at the same default width and grows only when its
// content (header or data) needs more room.
const MIN_COL_WIDTH = 120; // default width of each column (px) - change here

const fitTableSx = {
  width: "max-content",
  tableLayout: "auto",

  "& th, & td": {
    width: "auto !important",
    minWidth: MIN_COL_WIDTH,
  },

  "& th": { whiteSpace: "nowrap" },

  "& td": { maxWidth: 320, wordBreak: "break-word" },
};

// ── WhatsApp-specific table sizing ──────────────────────────────────────────
// Full-width fixed layout with explicit column widths (set through <colgroup>),
// so the table never collapses into a tiny box.
const whatsappTableSx = {
  width: "100%",
  minWidth: 760,
  tableLayout: "fixed",
  "& th": { whiteSpace: "nowrap" },
  "& td": { wordBreak: "break-word", verticalAlign: "middle" },
};

// Campaign ID | Campaign Name | Total Contacts | Status | Scheduled Time | Action
const WA_COL_WIDTHS = ["14%", "30%", "14%", "14%", "18%", "10%"];

const ScheduledSMS = () => {
  const [startdate, setStartDate]                   = useState(dayjs());
  const [enddate, setEndDate]                       = useState(dayjs());
  const [fileStatus]                                = useState("");
  const [service, setService]                       = useState("sms");
  const [deletingId, setDeletingId]                 = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog]   = useState(false);
  const [selectedSmsUploadId, setSelectedSmsUploadId] = useState(null);
  const [page, setPage]                             = useState(0);
  const [rowsPerPage, setRowsPerPage]               = useState(25);
  const [openTextDialog, setOpenTextDialog]         = useState(false);
  const [selectedText, setSelectedText]             = useState("");
  const [shouldFetch, setShouldFetch]               = useState(false);
  const [customerId, setCustomerId]                 = useState(null);
  const [whatsappStatuses, setWhatsappStatuses]     = useState(DEFAULT_WHATSAPP_STATUSES);

  // ── WhatsApp reschedule states ──
  const [openEditDialog, setOpenEditDialog]   = useState(false);
  const [editCampaign, setEditCampaign]       = useState(null);
  const [newScheduledTime, setNewScheduledTime] = useState(dayjs());
  const [isUpdating, setIsUpdating]           = useState(false);

  const startDateStr = startdate?.format("YYYY-MM-DD");
  const endDateStr   = enddate?.format("YYYY-MM-DD");
  const limit        = rowsPerPage;

  // ── Decode token ─────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) { toast.error("Access token not found"); return; }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) toast.error("Customer ID not found in token");
      else setCustomerId(id);
    } catch {
      toast.error("Invalid access token format");
    }
  }, []);

  // ── API hooks ─────────────────────────────────────────────────────────────────
  const {
    data: smsData,
    isLoading: smsLoading,
    isError: smsError,
    refetch: refetchSms,
  } = useFetchSmsUploadDataQuery(
    { startdate: startDateStr, enddate: endDateStr, file_status: fileStatus, page: page + 1, limit },
    { skip: !shouldFetch || service !== "sms" }
  );

  const whatsappStartDate = startdate
    ? dayjs.utc(startdate).startOf("day").toISOString()
    : undefined;
  const whatsappEndDate = enddate
    ? dayjs.utc(enddate).endOf("day").toISOString()
    : undefined;
  const whatsappDate =
    startdate && enddate && startdate === enddate ? startdate : undefined;
  const getWhatsappRequest = (requestPage) => ({
    customer_id: customerId,
    status: whatsappStatuses.length ? whatsappStatuses.join(",") : undefined,
    date: whatsappDate,
    start_date: whatsappStartDate,
    end_date: whatsappEndDate,
    page: requestPage,
    limit,
  });

  const {
    data: rcsData,
    isLoading: rcsLoading,
    isError: rcsError,
    refetch: refetchRcs,
  } = useGetrcsscheduleQuery(
    { startdate: startDateStr, enddate: endDateStr, page: page + 1, limit },
    { skip: !shouldFetch || service !== "rcs" }
  );

  const [whatsappTrigger, whatsappResult] = useLazyGetwhatsappscheduledcampaignsQuery();

  // ── Schedule Flow (lazy query so we control when it fires) ───────────────────
  const [flowTrigger, flowResult] = useLazyGetscheduleflowreportQuery();

  const { data: deleteKeyData } = useGetCustomerLogDeleteKeyQuery();
  const allowDelete = Boolean(deleteKeyData?.data?.allow_delete_logreport);

  const [deleteSmsUpload]        = useDeleteSmsUploadMutation();
  const [updatecampaignschedule] = useUpdateschedulecampaignMutation();

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleSearch = () => {
    setShouldFetch(true);
    setPage(0);
    if (service === "whatsapp") {
      if (!customerId) { toast.error("Customer ID is not available"); return; }
      whatsappTrigger(getWhatsappRequest(1));
    } else if (service === "flow") {
      flowTrigger({ startDate: startDateStr, endDate: endDateStr, page: 1, limit });
    } else {
      setTimeout(() => {
        if (service === "sms") refetchSms();
        else if (service === "rcs") refetchRcs();
      }, 0);
    }
  };

  // ── Re-fetch on page / rowsPerPage change for paginated services ─────────────
  useEffect(() => {
    if (!shouldFetch) return;
    if (service === "whatsapp" && customerId) {
      whatsappTrigger(getWhatsappRequest(page + 1));
    } else if (service === "flow") {
      flowTrigger({ startDate: startDateStr, endDate: endDateStr, page: page + 1, limit });
    }
  }, [page, rowsPerPage, service, shouldFetch, customerId, limit]); // eslint-disable-line

  const handleDelete = (id) => { setSelectedSmsUploadId(id); setOpenConfirmDialog(true); };

  const confirmDelete = async () => {
    if (!selectedSmsUploadId) return;
    try {
      setDeletingId(selectedSmsUploadId);
      const result = await deleteSmsUpload({ smsupload_id: selectedSmsUploadId }).unwrap();
      if (result.status) {
        toast.success(result.message);
        if (service === "sms") refetchSms();
        else if (service === "rcs") refetchRcs();
        else if (service === "whatsapp" && customerId)
          whatsappTrigger(getWhatsappRequest(page + 1));
      } else {
        toast.error(result.message || "Deletion failed");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.data?.message || "Failed to delete");
    } finally {
      setDeletingId(null);
      setOpenConfirmDialog(false);
      setSelectedSmsUploadId(null);
    }
  };

  const handleViewText = (text) => { setSelectedText(text); setOpenTextDialog(true); };
  const handleChangePage = (_, value) => setPage(value - 1);

  const handleRescheduleSubmit = async () => {
    if (!editCampaign || !newScheduledTime) return;
    if (newScheduledTime.isBefore(dayjs())) {
      toast.error("Scheduled time must be in the future");
      return;
    }
    const scheduledISO = newScheduledTime.toISOString();
    setIsUpdating(true);
    try {
      const res = await updatecampaignschedule({
        customer_id: customerId,
        campaign_id: editCampaign.campaign_id,
        scheduled_time: scheduledISO,
      }).unwrap();
      toast.success(res?.message || "Campaign rescheduled successfully");
      setOpenEditDialog(false);
      if (customerId) {
        whatsappTrigger(getWhatsappRequest(page + 1));
      }
    } catch (err) {
      toast.error(err?.data?.message || "Failed to reschedule campaign");
    } finally {
      setIsUpdating(false);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────────
  let currentData, currentLoading, currentError;
  switch (service) {
    case "sms":
      currentData = smsData; currentLoading = smsLoading; currentError = smsError; break;
    case "rcs":
      currentData = rcsData; currentLoading = rcsLoading; currentError = rcsError; break;
    case "whatsapp":
      currentData = whatsappResult.data; currentLoading = whatsappResult.isLoading; currentError = whatsappResult.isError; break;
    case "flow":
      currentData = flowResult.data; currentLoading = flowResult.isLoading; currentError = flowResult.isError; break;
    default:
      currentData = null; currentLoading = false; currentError = false;
  }

  const getPagination = () => {
    if (!currentData?.pagination) return { totalPages: 0, totalRecords: 0 };
    const p = currentData.pagination;
    if (service === "whatsapp") {
      return { totalPages: p.total_pages ?? 0, totalRecords: p.total ?? 0 };
    }
    // flow + sms + rcs all use totalPages / totalRecords
    return { totalPages: p.totalPages ?? 0, totalRecords: p.totalRecords ?? 0 };
  };
  const { totalPages, totalRecords } = getPagination();

  const isWhatsapp = service === "whatsapp";
  const isSms      = service === "sms";
  const isRcs      = service === "rcs";
  const isFlow     = service === "flow";

  // Derive dynamic order keys from the flow dataset
  const flowOrderKeys = isFlow ? getOrderKeys(currentData?.data ?? []) : [];

  // WhatsApp: show ONLY campaigns whose send time is in the future.
  // Past send times (or rows with no send time) are hidden.
  const whatsappRows = (currentData?.data ?? []).filter((row) => {
    if (!row.message_send_start_time) return false;
    const sendTime = dayjs(row.message_send_start_time);
    return sendTime.isValid() && sendTime.isAfter(dayjs());
  });

  const hasCurrentRows = isWhatsapp
    ? whatsappRows.length > 0
    : currentData?.data?.length > 0;

  // ── Column count per service ──────────────────────────────────────────────────
  const colSpanCount = isWhatsapp
    ? 6
    : isRcs
    ? 7
    : isFlow
    ? 4 + flowOrderKeys.length
    : 9;

  // ── Styles (mirrors the delivery report) ────────────────────────────────────
  const inputSx = {
    borderRadius: "0.5rem",
    minWidth: 100,
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.7)",
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#94A3B8" },
    },
    "& .MuiInputLabel-root": { color: "#475569" },
    "& .Mui-disabled": {
      "& .MuiOutlinedInput-input": { color: "#334155 !important", WebkitTextFillColor: "#334155 !important" },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
    },
  };

  const selectGlassSx = {
    height: 35,
    fontSize: "0.75rem",
    borderRadius: "8px",
    background: "rgba(255, 255, 255, 0.8)",
    transition: "all 0.2s ease",
    "& .MuiSelect-select": { py: 0.5, fontSize: "0.75rem" },
    "&:hover": { boxShadow: "0 4px 12px rgba(14,165,233,0.1)" },
    "&.Mui-focused": { boxShadow: "0 0 0 2px rgba(14,165,233,0.2)" },
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={layoutCSS}>
      <Typography variant="h6" sx={{ mb: 1, fontSize: "0.9rem", fontWeight: "bold" }}>
        Scheduled SMS Report
      </Typography>

      {/* ── Glass filter bar (mirrors SMS report page) ── */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          p: 2,
          mb: 2,
          borderRadius: "0.5rem",
          backgroundColor: "#ffffff52",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startdate}
            format="DD/MM/YYYY"
            sx={{ ...inputSx, width: 140 }}
            slotProps={{ textField: { size: "small" } }}
            onChange={(v) => {
              setStartDate(v);
              if (enddate && v && enddate.isBefore(v, "day")) setEndDate(v);
              else if (enddate && v && enddate.diff(v, "month", true) > 1) setEndDate(v.add(1, "month"));
            }}
          />
          <DatePicker
            label="End Date"
            value={enddate}
            format="DD/MM/YYYY"
            sx={{ ...inputSx, width: 140 }}
            maxDate={startdate ? startdate.add(1, "month") : null}
            minDate={startdate}
            slotProps={{ textField: { size: "small" } }}
            onChange={setEndDate}
          />
        </LocalizationProvider>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: "0.75rem" }}>Service</InputLabel>
          <Select
            value={service}
            onChange={(e) => { setService(e.target.value); setShouldFetch(false); setPage(0); }}
            label="Service"
            sx={selectGlassSx}
          >
            <MenuItem value="sms" sx={{ fontSize: "0.75rem" }}>SMS</MenuItem>
            <MenuItem value="rcs" sx={{ fontSize: "0.75rem" }}>RCS</MenuItem>
            <MenuItem value="whatsapp" sx={{ fontSize: "0.75rem" }}>WhatsApp</MenuItem>
            <MenuItem value="clicker" sx={{ fontSize: "0.75rem" }}>Clicker</MenuItem>
            <MenuItem value="flow" sx={{ fontSize: "0.75rem" }}>Schedule Flow</MenuItem>
          </Select>
        </FormControl>

        {service === "whatsapp" && (
          <FormControl size="small" sx={{ minWidth: 230 }}>
            <InputLabel id="whatsapp-status-label" sx={{ fontSize: "0.75rem" }}>Status</InputLabel>
            <Select
              labelId="whatsapp-status-label"
              multiple
              value={whatsappStatuses}
              label="Status"
              onChange={(event) => {
                setWhatsappStatuses(event.target.value);
                setShouldFetch(false);
                setPage(0);
              }}
              renderValue={(selected) => selected.join(", ")}
              sx={{ ...selectGlassSx, minHeight: 35 }}
            >
              {WHATSAPP_STATUSES.map((status) => (
                <MenuItem key={status} value={status} sx={{ fontSize: "0.75rem" }}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button
          variant="contained"
          startIcon={<SearchIcon fontSize="small" />}
          onClick={handleSearch}
          sx={{
            fontSize: "0.75rem",
            textTransform: "none",
            bgcolor: "#0288d1",
            "&:hover": { bgcolor: "#0277bd" },
            minWidth: 80,
          }}
        >
          Search
        </Button>
      </Paper>

      {/* ── Rows‑per‑page ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
          Showing {rowsPerPage} records per page
        </Typography>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            sx={inputSx}
          >
            {[25, 50, 100].map((n) => (
              <MenuItem key={n} value={n} sx={{ fontSize: "0.75rem" }}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper} sx={{ borderRadius: 0.3 }}>
        <Table size="small" sx={isWhatsapp ? whatsappTableSx : fitTableSx}>
          {isWhatsapp && (
            <colgroup>
              {WA_COL_WIDTHS.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
          )}
          <TableHead sx={{
            bgcolor: "#0288d1",
            "& th": { color: "white", fontSize: "0.78rem", padding: "7px 8px", borderBottom: "0.5px solid #1b1b1b", borderRight: "0.5px solid #1b1b1b" },
          }}>
            <TableRow>
              {/* ── Schedule Flow columns ── */}
              {isFlow ? (
                <>
                  <TableCell>Date</TableCell>
                  <TableCell>Time to Send</TableCell>
                  <TableCell>Flow ID</TableCell>
                  <TableCell>Campaign Name</TableCell>
                  {flowOrderKeys.map((key, i) => (
                    <TableCell key={key}>
                      Order {i + 1}
                    </TableCell>
                  ))}
                </>
              ) : isWhatsapp ? (
                <>
                  <TableCell>Campaign ID</TableCell>
                  <TableCell>Campaign Name</TableCell>
                  <TableCell align="center">Total Contacts</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Scheduled Time</TableCell>
                  <TableCell align="center">Action</TableCell>
                </>
              ) : isRcs ? (
                <>
                  <TableCell>Sr No</TableCell>
                  <TableCell>Campaign Name</TableCell>
                  <TableCell>Bot Name</TableCell>
                  <TableCell>Template Name</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Scheduled Time</TableCell>
                  <TableCell>Campaign Category</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Date</TableCell>
                  <TableCell>Campaign ID</TableCell>
                  <TableCell>Total Numbers</TableCell>
                  <TableCell>Msg Count</TableCell>
                  <TableCell>Total Count</TableCell>
                  <TableCell>Template Name</TableCell>
                  <TableCell>SenderID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {!shouldFetch ? (
              <TableRow>
                <TableCell colSpan={colSpanCount} align="center" sx={cellSX}>
                  Click Search to load data
                </TableCell>
              </TableRow>
            ) : currentLoading ? (
              <TableRow>
                <TableCell colSpan={colSpanCount} align="center" sx={cellSX}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : currentError ? (
              <TableRow>
                <TableCell colSpan={colSpanCount} align="center" sx={cellSX}>
                  Error loading data
                </TableCell>
              </TableRow>
            ) : hasCurrentRows ? (
              /* ════════════════ SCHEDULE FLOW ROWS ════════════════ */
              isFlow ? (
                currentData.data.map((row, index) => (
                  <TableRow key={row.campaignflow_id || index} hover>
                    {/* Date */}
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" display="block" fontWeight={600} sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
                        {row.date ? dayjs(row.date).format("DD/MM/YYYY") : "—"}
                      </Typography>
                    </TableCell>

                    {/* Time to Send */}
                    <TableCell sx={cellSX}>
                      {row.time_to_send ? (
                        <>
                          <Typography variant="caption" display="block" fontWeight={600} sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
                            {dayjs(row.time_to_send).format("DD/MM/YYYY")}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}>
                            {dayjs(row.time_to_send).format("HH:mm:ss")}
                          </Typography>
                        </>
                      ) : "—"}
                    </TableCell>

                    {/* Flow ID */}
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" fontWeight={700} sx={{ fontSize: "0.75rem", color: "#1e40af" }}>
                        {row.campaignflow_id || "—"}
                      </Typography>
                    </TableCell>

                    {/* Campaign Name */}
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {row.campaign_name || "—"}
                      </Typography>
                    </TableCell>

                    {/* Dynamic order columns */}
                    {flowOrderKeys.map((key, i) => (
                      <TableCell key={key} sx={cellSX}>
                        <OrderChip label={row[key]} colorIdx={i} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isWhatsapp ? (
                /* ════════════════ WHATSAPP ROWS ════════════════ */
                whatsappRows.map((row, index) => (
                  <TableRow key={row.campaign_id || index} hover>
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: "0.75rem" }}>
                        {row.campaign_id || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {row.campaign_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSX, textAlign: "center" }}>
                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: "0.75rem" }}>
                        {row.total_contacts ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSX, textAlign: "center" }}>
                      <Chip
                        label={row.status || "Pending"}
                        size="small"
                        color={statusColor(row.status)}
                        sx={{ height: 20, fontSize: "0.65rem", "& .MuiChip-label": { px: 1 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSX, textAlign: "center" }}>
                      <Typography variant="caption" display="block" fontWeight={600} sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
                        {dayjs(row.message_send_start_time).format("DD/MM/YYYY")}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}>
                        {dayjs(row.message_send_start_time).format("HH:mm:ss")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...cellSX, textAlign: "center" }}>
                      <Tooltip title="Edit Campaign">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setEditCampaign(row);
                            setNewScheduledTime(
                              row.message_send_start_time ? dayjs(row.message_send_start_time) : dayjs()
                            );
                            setOpenEditDialog(true);
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : isRcs ? (
                /* ════════════════ RCS ROWS ════════════════ */
                currentData.data.map((row, index) => (
                  <TableRow key={row.campaign_id || index} hover>
                    <TableCell sx={{ ...cellSX, textAlign: "center" }}>
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {row.campaign_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {row.BotName || row.bot_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {row.template_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSX}>
                      {row.created_at ? (
                        <>
                          <Typography variant="caption" display="block" fontWeight={600} sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
                            {dayjs(row.created_at).format("DD/MM/YYYY")}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}>
                            {dayjs(row.created_at).format("HH:mm:ss")}
                          </Typography>
                        </>
                      ) : "—"}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      {row.executed_at || row.scheduled_time ? (
                        <>
                          <Typography variant="caption" display="block" fontWeight={600} sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
                            {dayjs(row.executed_at || row.scheduled_time).format("DD/MM/YYYY")}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}>
                            {dayjs(row.executed_at || row.scheduled_time).format("HH:mm:ss")}
                          </Typography>
                        </>
                      ) : "—"}
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                        {row.category || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                /* ════════════════ SMS ROWS ════════════════ */
                currentData.data.map((row, index) => (
                  <TableRow key={row.smsupload_id || index} hover>
                    <TableCell sx={cellSX}>
                      <Typography variant="caption" display="block" fontWeight={600} sx={{ fontSize: "0.72rem", lineHeight: 1.4 }}>
                        {dayjs(row.time_to_send).format("DD/MM/YYYY")}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: "0.68rem", lineHeight: 1.4 }}>
                        {dayjs(row.time_to_send).format("HH:mm:ss")}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSX}>{row.smsupload_id}</TableCell>
                    <TableCell sx={cellSX}>{row.total_mobiles}</TableCell>
                    <TableCell sx={cellSX}>{row.msg_count}</TableCell>
                    <TableCell sx={cellSX}>{row.total_count}</TableCell>
                    <TableCell sx={cellSX}>
                      {row.template_name || "—"}
                      <Tooltip title="View SMS Text">
                        <IconButton size="small" onClick={() => handleViewText(row.text)} sx={{ ml: 0.5 }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellSX}>{row.senderID}</TableCell>
                    <TableCell sx={cellSX}>
                      <Chip
                        label={row.file_status || "Pending"}
                        size="small"
                        color={statusColor(row.file_status)}
                        sx={{ height: 20, fontSize: "0.65rem", "& .MuiChip-label": { px: 1 } }}
                      />
                    </TableCell>
                    <TableCell sx={cellSX}>
                      <IconButton
                        onClick={() => handleDelete(row.smsupload_id)}
                        disabled={deletingId === row.smsupload_id}
                        size="small"
                        sx={{
                          color: deletingId === row.smsupload_id ? "#ccc" : "error.main",
                          position: "relative",
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                        {deletingId === row.smsupload_id && (
                          <CircularProgress
                            size={20}
                            sx={{ position: "absolute", top: "50%", left: "50%", mt: "-10px", ml: "-10px" }}
                          />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )
            ) : (
              <TableRow>
                <TableCell colSpan={colSpanCount} align="center" sx={cellSX}>
                  {isWhatsapp
                    ? "No upcoming scheduled campaigns found for the selected dates"
                    : "No data found for the selected criteria"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {shouldFetch && totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1 }}>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={handleChangePage}
              color="primary"
              shape="rounded"
              size="small"
            />
            <Typography sx={{ fontSize: "0.75rem" }}>
              Total records: {totalRecords}
            </Typography>
          </Box>
        )}
      </TableContainer>

      {/* ── Confirm Delete Dialog ── */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this SMS upload?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="primary">Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>Yes</Button>
        </DialogActions>
      </Dialog>

      {/* ── Reschedule WhatsApp Campaign Dialog ── */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="xs"
        fullWidth
        sx={{ "& .MuiDialog-container": { alignItems: "flex-start", paddingTop: "5vh" } }}
      >
        <DialogTitle>Reschedule Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set a new send time for campaign <strong>{editCampaign?.campaign_id}</strong>.
          </DialogContentText>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="New Scheduled Time"
              value={newScheduledTime}
              onChange={(newValue) => setNewScheduledTime(newValue)}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              minDateTime={dayjs()}
              sx={{ width: "100%" }}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} color="inherit" disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleRescheduleSubmit}
            variant="contained"
            color="primary"
            disabled={isUpdating || !newScheduledTime}
            startIcon={isUpdating ? <CircularProgress size={18} /> : null}
          >
            {isUpdating ? "Updating…" : "Reschedule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Text Viewer Dialog ── */}
      <Dialog open={openTextDialog} onClose={() => setOpenTextDialog(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <DialogContentText>{selectedText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTextDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledSMS;